import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import * as merchantService from "@/shared/api/services/merchantService";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Calendar, ChevronLeft, ChevronRight, X, ListFilter, BarChart3, ArrowDownUp, Layers } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Calendar as Cal } from "@/shared/components/ui/calendar";
import { format } from "date-fns";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import { auth } from "@/shared/api/graphqlClient";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');

.tx {
  font-family: 'DM Sans', sans-serif;
  padding-bottom: 60px;
  --cyan: #22d3ee;
  --surface: rgba(8,16,38,0.96);
  --surface-2: rgba(11,22,50,0.95);
  --border: rgba(34,211,238,0.1);
  --text-1: #e8f4ff;
  --text-2: #7aaec8;
  --text-3: #3a5a78;
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}
.tx * { box-sizing: border-box; }

/* ── Title ── */
.tx-h1 {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.85rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: #eaf6ff;
}
.tx-sub { 
  color: #eaf6ff;       /* header jaisa bright light blue */
  font-family: 'DM Sans', sans-serif;
  font-size: 13px; 
  margin-top: 6px; 
  font-weight: 500; 
}

/* ── Summary badges ── */
.tx-summary {
  display: flex; flex-wrap: wrap; gap: 12px; margin-top: 2px;
}
.tx-badge {
  display: flex; align-items: center; gap: 7px;
  background: rgba(34,211,238,0.05);
  border: 1px solid rgba(34,211,238,0.12);
  border-radius: 10px; padding: 7px 14px;
  font-size: 12px; font-weight: 600; color: var(--text-3);
  cursor: default;
  transition: all 0.25s var(--spring);
}
.tx-badge:hover {
  border-color: rgba(34,211,238,0.28); color: var(--text-2);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(34,211,238,0.08);
}
.tx-badge-dot {
  width: 6px; height: 6px; border-radius: 50%;
}

/* ── Filter bar ── */
.tx-bar {
  background: linear-gradient(145deg, var(--surface), var(--surface-2));
  border: 1px solid var(--border);
  border-radius: 20px; padding: 16px 20px;
  display: flex; flex-wrap: wrap; gap: 10px; align-items: center;
  position: relative; overflow: hidden;
  transition: border-color 0.3s, box-shadow 0.3s;
}
.tx-bar::before {
  content: '';
  position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent);
}
.tx-bar:focus-within {
  border-color: rgba(34,211,238,0.2);
  box-shadow: 0 4px 36px rgba(34,211,238,0.07);
}

/* ── Search input ── */
.tx-s-wrap { position: relative; flex: 1; min-width: 200px; }
.tx-s-ico {
  position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
  width: 15px; height: 15px; color: var(--text-3); pointer-events: none;
}
.tx-s-in {
  width: 100%;
  background: rgba(34,211,238,0.04);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 12px; padding: 10px 14px 10px 40px;
  color: var(--text-1);
  font-family: 'DM Sans', sans-serif; font-size: 13.5px;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}
.tx-s-in::placeholder { color: var(--text-3); }
.tx-s-in:focus {
  border-color: rgba(34,211,238,0.38);
  background: rgba(34,211,238,0.07);
  box-shadow: 0 0 0 4px rgba(34,211,238,0.08), 0 6px 24px rgba(34,211,238,0.08);
}

/* ── Selects ── */
.tx-sel {
  background: rgba(34,211,238,0.04) !important;
  border: 1px solid rgba(34,211,238,0.1) !important;
  color: var(--text-1) !important; border-radius: 12px !important;
  font-family: 'DM Sans', sans-serif !important; font-size: 13px !important;
  min-width: 130px;
  transition: border-color 0.22s, box-shadow 0.22s, transform 0.3s var(--spring) !important;
  will-change: transform;
}
.tx-sel:hover {
  border-color: rgba(34,211,238,0.28) !important;
  background: rgba(34,211,238,0.08) !important;
  box-shadow: 0 4px 16px rgba(34,211,238,0.1) !important;
  transform: translateY(-1px) !important;
}

/* ── Date button ── */
.tx-date {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 12px; padding: 10px 14px; color: var(--text-3);
  font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  transition: all 0.25s var(--spring);
}
.tx-date:hover {
  background: rgba(34,211,238,0.09); border-color: rgba(34,211,238,0.3);
  color: var(--cyan); box-shadow: 0 4px 18px rgba(34,211,238,0.1);
  transform: translateY(-2px);
}
.tx-date svg { width: 14px; height: 14px; }

/* ── Clear btn ── */
.tx-clr {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(244,63,94,0.06); border: 1px solid rgba(244,63,94,0.18);
  border-radius: 12px; padding: 10px 14px;
  color: #fca5a5; font-family: 'DM Sans', sans-serif;
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  transition: all 0.22s ease;
}
.tx-clr:hover {
  background: rgba(244,63,94,0.12); border-color: rgba(251,113,133,0.38);
  transform: scale(1.03);
}
.tx-clr svg { width: 13px; height: 13px; }

/* ── Pagination ── */
.tx-pag { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.tx-pag-info { color: var(--text-3); font-size: 12.5px; font-weight: 500; }
.tx-arr {
  width: 34px; height: 34px; border-radius: 10px;
  background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1);
  display: flex; align-items: center; justify-content: center;
  color: var(--text-3); cursor: pointer;
  transition: all 0.25s var(--spring);
}
.tx-arr:hover:not(:disabled) {
  background: rgba(34,211,238,0.12); border-color: rgba(34,211,238,0.32);
  color: var(--cyan); box-shadow: 0 4px 14px rgba(34,211,238,0.15);
  transform: scale(1.1);
}
.tx-arr:disabled { opacity: 0.25; cursor: not-allowed; }
.tx-arr svg { width: 15px; height: 15px; }
.tx-pg {
  min-width: 32px; height: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12.5px; font-weight: 700; cursor: pointer;
  border: 1px solid transparent; color: var(--text-3);
  transition: all 0.2s var(--spring);
}
.tx-pg:hover { background: rgba(34,211,238,0.09); color: var(--cyan); transform: scale(1.1); }
.tx-pg.on {
  background: linear-gradient(135deg, rgba(14,165,233,0.2), rgba(34,211,238,0.1));
  border-color: rgba(34,211,238,0.3); color: var(--cyan);
  box-shadow: 0 4px 14px rgba(34,211,238,0.18);
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .tx-h1 { font-size: 1.5rem; }
  .tx-bar { padding: 12px 14px; border-radius: 16px; }
  .tx-s-wrap { min-width: 100%; }
}
`;

const ddSty = {
  background: "rgba(8,16,38,0.98)",
  border: "1px solid rgba(34,211,238,0.15)",
  borderRadius: 16,
  color: "#e8f4ff",
  fontFamily: "'DM Sans', sans-serif",
  boxShadow: "0 20px 60px rgba(0,0,0,0.75)",
  backdropFilter: "blur(20px)",
};

export default function MerchantTransactions() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [dr, setDr] = useState({ from: null, to: null });
  const [selTx, setSelTx] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const merchantId = auth.getUserData()?.userId;

  const filters = React.useMemo(() => {
    const f = {};
    if (status !== "all") f.status = status;
    if (type !== "all") f.type = type;
    if (dr.from) f.startDate = dr.from.toISOString();
    if (dr.to) f.endDate = dr.to.toISOString();
    return f;
  }, [status, type, dr]);

  const { data: combinedData, isLoading: isLoadingCombined } = useQuery({
    queryKey: ["merchant-combined-transactions", page, pageSize, search, filters, merchantId],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      page + 1,
      pageSize,
      search,
      filters,
      [merchantId]
    ),
    enabled: !!merchantId,
    keepPreviousData: true,
  });

  const totalItems = combinedData?.total || 0;
  const totalPages = combinedData?.totalPages || 0;
  const rawData = combinedData?.data || [];

  const transactions = React.useMemo(() => {
    return rawData.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id,
      type: t.type?.toLowerCase() === 'sale' ? 'deposit' : (t.type?.toLowerCase() || "unknown"),
      status: (t.status || "").toLowerCase(),
      amount: t.amount,
      net_amount: Number(t.net_amount ?? 0),
      total_fee: Number(t.total_fee ?? 0),
      provider_display: t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
      is_qr_code: t.type?.toLowerCase() === 'sale' || t.type?.toLowerCase() === 'deposit',
    })).sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
  }, [rawData]);

  const hasF = search || status !== "all" || type !== "all" || dr.from;
  const clear = () => { setSearch(""); setStatus("all"); setType("all"); setDr({ from: null, to: null }); setPage(0); };

  const nums = React.useMemo(() => {
    const a = [];
    for (let i = Math.max(0, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) a.push(i);
    return a;
  }, [page, totalPages]);

  // Quick stats - Note: These will now reflect the current filtered/paged data or we might need a separate total stats query if we want global counts
  // For now, let's keep them reflecting the current paged data as a fallback, or better, remove the "Success/Pending/Deposits/Withdrawals" badges if they don't make sense per page,
  // or just use the items we have.
  const successCount = transactions.filter(t => t.status === "success").length;
  const pendingCount = transactions.filter(t => t.status === "pending" || t.status === "processing").length;
  const depCount = transactions.filter(t => t.type === "deposit").length;
  const witCount = transactions.filter(t => t.type === "withdrawal").length;

  return (
    <MerchantLayout currentPage="MerchantTransactions" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{CSS}</style>
      <div className="tx">
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 16, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                boxShadow: "0 10px 30px rgba(6,182,212,0.4)",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg,rgba(255,255,255,0.15),transparent)" }} />
                <BarChart3 size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
              </div>
              <div>
                <div className="tx-h1">{t("transactions")}</div>
                <div className="tx-sub">{totalItems} {t("transactionsFound")}</div>
              </div>
            </div>

            {/* Summary badges */}
            <div className="tx-summary">
              {[
                { dot: "#10b981", label: `${successCount} Success` },
                { dot: "#f59e0b", label: `${pendingCount} Pending` },
                { dot: "#22d3ee", label: `${depCount} Deposits` },
                { dot: "#a78bfa", label: `${witCount} Withdrawals` },
              ].map(({ dot, label }) => (
                <motion.div
                  key={label} className="tx-badge"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 400, damping: 22 }}
                >
                  <div className="tx-badge-dot" style={{ background: dot, boxShadow: `0 0 8px ${dot}` }} />
                  {label}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div className="tx-bar"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.09, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <ListFilter style={{ width: 16, height: 16, color: "#3a5a78", flexShrink: 0 }} />

            <div className="tx-s-wrap">
              <Search className="tx-s-ico" />
              <input
                className="tx-s-in"
                placeholder={t("searchHeaderPlaceholder")}
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
              />
            </div>

            <Select value={status} onValueChange={v => { setStatus(v); setPage(0); }}>
              <SelectTrigger className="tx-sel" style={{ width: 132 }}><SelectValue placeholder={t("status")} /></SelectTrigger>
              <SelectContent style={ddSty}>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="declined">{t("declined")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={type} onValueChange={v => { setType(v); setPage(0); }}>
              <SelectTrigger className="tx-sel" style={{ width: 132 }}><SelectValue placeholder={t("type")} /></SelectTrigger>
              <SelectContent style={ddSty}>
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="deposit">{t("deposit")}</SelectItem>
                <SelectItem value="withdrawal">{t("withdrawal")}</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <button className="tx-date">
                  <Calendar />
                  {dr.from ? dr.to ? `${format(dr.from, "MMM d")} – ${format(dr.to, "MMM d")}` : format(dr.from, "MMM d") : t("dateRange")}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" style={{ background: "rgba(8,16,38,0.98)", border: "1px solid rgba(34,211,238,0.15)", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,0.75)", backdropFilter: "blur(24px)" }}>
                <Cal mode="range" selected={dr} onSelect={r => { setDr(r || { from: null, to: null }); setPage(0); }} numberOfMonths={2} />
              </PopoverContent>
            </Popover>

            <AnimatePresence>
              {hasF && (
                <motion.button className="tx-clr" onClick={clear}
                  initial={{ opacity: 0, scale: 0.82, x: 10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.82, x: 10 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                >
                  <X />{t("clearFilters")}
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Table */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <TransactionTable transactions={transactions} isLoading={isLoadingCombined} onRowClick={setSelTx} />
          </motion.div>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div className="tx-pag" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}>
              <div className="tx-pag-info">
                {t("showing")} {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalItems)} {t("of")} {totalItems}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button className="tx-arr" onClick={() => setPage(p => p - 1)} disabled={page === 0}><ChevronLeft /></button>
                {nums.map(n => (
                  <motion.button
                    key={n}
                    className={`tx-pg${n === page ? " on" : ""}`}
                    onClick={() => setPage(n)}
                    whileTap={{ scale: 0.92 }}
                  >
                    {n + 1}
                  </motion.button>
                ))}
                <button className="tx-arr" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}><ChevronRight /></button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <TransactionDetailPanel transaction={selTx} open={!!selTx} onClose={() => setSelTx(null)} />
    </MerchantLayout>
  );
}